import { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import type { AuditEvent } from '@frontend/api-phone-audits';
import { EventListItem, MARKER_SIZE, TIME_ROW_HEIGHT } from './event-list-item';

type EventDetailsListProps = {
  events: AuditEvent[];
  activeEventId?: string;
};

/**
 * Renders a vertical timeline list of audit events for the history details tray.
 *
 * A single continuous vertical line is drawn from the center of the first marker to the
 * center of the last marker via a pseudo-element on the container. Each item's circular
 * marker sits on top of the line (via z-index). The list is scrollable via the parent's
 * overflow container.
 */
export const EventDetailsList = ({ events, activeEventId }: EventDetailsListProps) => {
  const activeRef = useRef<HTMLLIElement>(null);
  const needsScroll = !!activeEventId;
  const [isPositioned, setIsPositioned] = useState(!needsScroll);

  useEffect(() => {
    if (!activeEventId || !activeRef.current) {
      setIsPositioned(true);
      return;
    }
    // Use requestAnimationFrame to ensure the DOM has laid out before scrolling.
    // Instantly scroll (no animation) so the list appears already at the right spot,
    // then reveal the content.
    const rafId = requestAnimationFrame(() => {
      activeRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      setIsPositioned(true);
    });
    return () => cancelAnimationFrame(rafId);
  }, [activeEventId, events]);

  return (
    <ul
      css={css`
        list-style: none;
        margin: 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        position: relative;
        gap: var(--fabric-space-gap-lg);
        opacity: ${isPositioned ? 1 : 0};
        transition: opacity 0.15s ease-in;

        /* Single continuous vertical timeline line */
        &::before {
          content: '';
          position: absolute;
          /* Horizontally centered on the marker column (half of MARKER_SIZE) */
          left: ${MARKER_SIZE / 2}px;
          transform: translateX(-50%);
          /* Start at the center of the first marker */
          top: ${TIME_ROW_HEIGHT + MARKER_SIZE / 2}px;
          /* End at the center of the last marker — bottom offset accounts for the last item's
          remaining height below its marker center. */
          bottom: ${MARKER_SIZE / 2}px;
          width: 1px;
          background-color: var(--fabric-color-border-default);
        }
      `}
    >
      {events.map((event, index) => {
        const isActive = !!activeEventId && event.id === activeEventId;
        return (
          <EventListItem
            key={event.id ?? index}
            ref={isActive ? activeRef : undefined}
            event={event}
            isHighlighted={isActive}
          />
        );
      })}
    </ul>
  );
};
